import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import DashboardCard from '../../components/Dashboard/DashboardCard'
import CustomTable from '../../components/Table'
import CustomTableBody from '../../components/Table/TableBody'
import CustomTableData from '../../components/Table/TableData'
import CustomTableHead from '../../components/Table/TableHead'
import CustomTableRow from '../../components/Table/TableRow'
import { formatDate } from '../../utils/formatDate'
import { getLocal } from '../../utils/local'
import { DashboardStatsData } from '../../interfaces/dashboard'
import DashboardService from '../../api/dashboard-Service'
import { getDashboardCardData } from '../../utils/dashboardCardConfig'
import SkeletonCard from './SkeletonCard'
import { handleErrorResponse } from '../../utils/handleErrorResponse'
import { getFallbackStatsData } from '../../utils/fallbackStat'
import { ViewStaffsSkeletonLoader } from '../../components/Loader/ViewStaffsSkeletonLoader'
import Empty from '../../assets/icons/Empty'
import SecurityService from '../../api/security'
import { getStatusTextClass } from '../../utils/getStatusColor'
import Metadata from '../../components/Metadata'
import PageWrapper from '../../components/PageWrapper'

const Dashboard = () => {
  const [stats, setStats] = useState<DashboardStatsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const tableHeadData = ['Name/Email of Resident', 'Name of Guest', 'Date', 'Address', 'Status']

  useEffect(() => {
    const estateId = getLocal('estate')?.id
    if (!estateId) return

    DashboardService.getEstateStat(estateId)
      .then((res) => {
        setStats(res.data)
        setHasError(false)
      })
      .catch((err) => {
        handleErrorResponse(err, 'dashboard-error')
        setHasError(true)
      })
      .finally(() => setLoading(false))
  }, [])

  const { data, isLoading } = SecurityService.useGetGuestList({ page: 1, limit: 5 })

  const displayStats = hasError || !stats ? getFallbackStatsData() : stats


  const dashboardMetadata = {
    title: 'Dashboard - Safelto',
    description: 'Get an overview of your estate activities, guest management, and property statistics. Manage your residential community efficiently.',
    keywords: ['dashboard', 'estate management', 'property statistics', 'guest management', 'residential community'],
    image: '/assets/images/safelto-big.png',
    url: 'https://safelto.com/dashboard',
    type: 'website' as 'website'
  }

  return (
    <PageWrapper title="Dashboard" description="Here is an overview of your weekly activities">
      <Metadata {...dashboardMetadata} />
      <div className="">
        <div
          className="flex gap-4 md:gap-[42px] justify-between
          overflow-x-auto whitespace-nowrap md:whitespace-normal
          scroll-smooth scrollbar-hide md:-mx-4 px-4"
        >
          {loading
            ? [...Array(4)].map((_, index) => <SkeletonCard key={index} />)
            : getDashboardCardData(displayStats).map((item, index) => (
                <DashboardCard key={index} {...item} />
              ))}
        </div>

        {/* Guests Table */}
        <div className="mt-12">
          <div>
            <h2 className="font-semibold text-[28px]">Guests</h2>
            <div className="flex justify-between mb-4">
              <p className="text-[#6C757D] font-medium">Showing last 5 guests</p>
              <Link to="/security" className="text-[#00A1FF]">
                View all
              </Link>
            </div>
          </div>

          {isLoading ? (
            <ViewStaffsSkeletonLoader />
          ) : data?.results?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[300px] w-full gap-4">
              <Empty />
              <p className="text-sm font-semibold">No results found</p>
            </div>
          ) : (
            <>
              <CustomTable>
                <CustomTableHead dataList={tableHeadData} />
                <CustomTableBody loading={isLoading}>
                  {data?.results.slice(0,5).map((guest) => (
                    <CustomTableRow
                      key={guest.id}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <CustomTableData dataLabel="Name/Email of resident" text="" className="px-6">
                        <div>
                          <div>{`${guest.host.firstName} ${guest.host.lastName}`}</div>
                          <div>{guest.host.email || 'N/A'}</div>
                        </div>
                      </CustomTableData>
                      <CustomTableData dataLabel="Name of guest" text={guest.name} className="px-6" />
                      <CustomTableData
                        dataLabel="Date"
                        text={formatDate(guest.startsAt)}
                        className="px-6"
                      />
                      <CustomTableData
                        dataLabel="Address"
                        text={guest.property?.address || 'N/A'}
                        className="px-6"
                      />
                      <CustomTableData dataLabel={'Status'} text="" className="px-6">
                        <p className={getStatusTextClass(guest.status)}>
                          {guest.status.charAt(0).toUpperCase() + guest.status.slice(1)}
                        </p>
                      </CustomTableData>
                    </CustomTableRow>
                  ))}
                </CustomTableBody>
              </CustomTable>
            </>
          )}
        </div>
      </div>
    </PageWrapper>
  )
}

export default Dashboard

